import type { ProjectsWipTableDataQuery } from "@src/__generated__/graphql";
import { action, makeObservable, observable } from "mobx";

type TProjectWip = NonNullable<
  ProjectsWipTableDataQuery["projectsWithPeriods"]
>["data"][0];

export class ProjectWip {
  id: string;
  userIds: string[];

  total_budget: TProjectWip["total_budget"];
  total_budget_change: TProjectWip["total_budget_change"];
  spent_budget: TProjectWip["spent_budget"];
  spent_budget_percent: TProjectWip["spent_budget_percent"];
  spent_time: TProjectWip["spent_time"];
  remaining_budget: TProjectWip["remaining_budget"];
  remaining_time: TProjectWip["remaining_time"];
  @observable.ref progress_percent: TProjectWip["progress_percent"];
  @observable.ref progress_change: TProjectWip["progress_change"];
  @observable.ref
  progress_change_percent: TProjectWip["progress_change_percent"];
  profit_estimate: TProjectWip["profit_estimate"];
  profit_estimate_percent: TProjectWip["profit_estimate_percent"];

  is_closed: TProjectWip["is_closed"];
  is_locked: TProjectWip["is_locked"];

  project: TProjectWip["project"];

  __subRows: SubRow[];

  constructor(src: TProjectWip) {
    makeObservable(this);
    this.id = src.project.id;
    this.userIds = [
      src.project.projectManager.id,
      ...(src.project.otherProjectManagers || []).map((i) => i.id),
    ];
    this.total_budget = src.total_budget;
    this.total_budget_change = src.total_budget_change;
    this.spent_budget = src.spent_budget;
    this.spent_budget_percent = src.spent_budget_percent;
    this.spent_time = src.spent_time;
    this.remaining_budget = src.remaining_budget;
    this.remaining_time = src.remaining_time;
    this.progress_percent = src.progress_percent;
    this.progress_change = src.progress_change;
    this.progress_change_percent = src.progress_change_percent;
    this.profit_estimate = src.profit_estimate;
    this.profit_estimate_percent = src.profit_estimate_percent;
    this.is_locked = src.is_locked;
    this.is_closed = src.is_closed;
    this.project = src.project;
    this.__subRows = (src.budgetItemsStats ?? []).map((i) => new SubRow(i));
  }

  @action.bound
  setProgressPercent(value: ProjectWip["progress_percent"]) {
    this.progress_percent = value;
  }

  @action.bound
  setProgressChange(value: ProjectWip["progress_change"]) {
    this.progress_change = value;
  }

  @action.bound
  setProgressChangePercent(value: ProjectWip["progress_change_percent"]) {
    this.progress_change_percent = value;
  }
}

type TBudgetItemWithPeriod = NonNullable<TProjectWip["budgetItemsStats"]>[0];

export class SubRow {
  our_work_budget_item_id: TBudgetItemWithPeriod["our_work_budget_item_id"];
  work_in_progress_id: TBudgetItemWithPeriod["work_in_progress_id"];
  title: TBudgetItemWithPeriod["title"];

  total_budget: TBudgetItemWithPeriod["total_budget"];
  total_budget_change: TBudgetItemWithPeriod["total_budget_change"];
  spent_budget: TBudgetItemWithPeriod["spent_budget"];
  spent_budget_percent: TBudgetItemWithPeriod["spent_budget_percent"];
  spent_time: TBudgetItemWithPeriod["spent_time"];
  remaining_budget: TBudgetItemWithPeriod["remaining_budget"];
  remaining_time: TBudgetItemWithPeriod["remaining_time"];
  @observable.ref progress_percent: TBudgetItemWithPeriod["progress_percent"];
  @observable.ref progress_change: TBudgetItemWithPeriod["progress_change"];
  @observable.ref
  progress_change_percent: TBudgetItemWithPeriod["progress_change_percent"];
  profit_estimate: TBudgetItemWithPeriod["profit_estimate"];
  profit_estimate_percent: TBudgetItemWithPeriod["profit_estimate_percent"];

  is_locked: TBudgetItemWithPeriod["is_locked"];
  is_closed: TBudgetItemWithPeriod["is_closed"];
  deleted: TBudgetItemWithPeriod["deleted"];

  constructor(src: TBudgetItemWithPeriod) {
    makeObservable(this);
    this.our_work_budget_item_id = src.our_work_budget_item_id;
    this.work_in_progress_id = src.work_in_progress_id;
    this.title = src.title;
    this.total_budget = src.total_budget;
    this.total_budget_change = src.total_budget_change;
    this.spent_budget = src.spent_budget;
    this.spent_budget_percent = src.spent_budget_percent;
    this.spent_time = src.spent_time;
    this.remaining_budget = src.remaining_budget;
    this.remaining_time = src.remaining_time;
    this.progress_percent = src.progress_percent;
    this.progress_change = src.progress_change;
    this.progress_change_percent = src.progress_change_percent;
    this.profit_estimate = src.profit_estimate;
    this.profit_estimate_percent = src.profit_estimate_percent;
    this.is_closed = src.is_closed;
    this.is_locked = src.is_locked;
    this.deleted = src.deleted;
  }

  get id() {
    return this.our_work_budget_item_id;
  }

  @action
  setProgressPercent(value: SubRow["progress_percent"]) {
    this.progress_percent = value;
  }

  @action
  setProgressChange(value: SubRow["progress_change"]) {
    this.progress_change = value;
  }

  @action
  setProgressChangePercent(value: SubRow["progress_change_percent"]) {
    this.progress_change_percent = value;
  }
}
